import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pathways = ["An individual in the messy middle", "A leader", "An organisation", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", pathway: "Not sure yet", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Work With Alex — People Growing People";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError("Something didn't go through. Please try again — your words matter here.");
    }
  };

  const field =
    "w-full bg-transparent border-b border-line focus:border-clay outline-none py-4 text-base md:text-lg placeholder:text-stone/60 transition-colors duration-300";

  return (
    <main data-testid="contact-page" className="pt-20">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <MaskedLine delay={0.1}>
            <span className="text-xs uppercase tracking-[0.3em] text-stone">Work With Alex</span>
          </MaskedLine>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] mt-6">
            <MaskedLine delay={0.25}>Tell me</MaskedLine>
            <MaskedLine delay={0.37}>
              <span className="italic text-clay">where you are.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/75 max-w-md">
              No sales funnel. No pressure. Just the beginning of an honest
              conversation about where you are, what's disrupting you, and what
              might be possible from here.
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="mt-12 space-y-0 border-t border-line max-w-md">
              {[
                "You will be seen before you are sold to.",
                "Safe, but not passive. Compassionate, but honest.",
                "Maybe you can take another step. Start here.",
              ].map((t) => (
                <div key={t} className="py-5 border-b border-line flex gap-4 items-baseline">
                  <span className="w-1.5 h-1.5 bg-clay shrink-0 translate-y-[-2px]" />
                  <p className="text-sm md:text-base text-ink/70 leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="mt-12 max-w-xs">
              <div className="overflow-hidden border border-line">
                <img
                  src="/alex-smile.jpg"
                  alt="Alex — the person who will read your message"
                  loading="lazy"
                  className="w-full aspect-[4/5] object-cover"
                  data-testid="contact-alex-photo"
                />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.22em] text-stone">
                Every message is read by Alex personally
              </p>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 md:pl-12 md:border-l md:border-line">
          {status === "sent" ? (
            <Reveal>
              <div data-testid="contact-success" className="border border-line bg-sand/50 p-10 md:p-14">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-moss text-paper">
                  <Check className="w-5 h-5" />
                </span>
                <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-8">
                  Thank you. <span className="italic text-moss">Message received.</span>
                </h2>
                <p className="mt-6 text-base md:text-lg text-ink/70 leading-relaxed max-w-md">
                  Alex reads every enquiry personally and will be in touch soon.
                  Until then — be gentle with yourself in the middle.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.3}>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-10">
                <div>
                  <label htmlFor="name" className="text-xs uppercase tracking-[0.25em] text-stone">
                    Your name
                  </label>
                  <input
                    id="name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="What should Alex call you?"
                    className={field}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="text-xs uppercase tracking-[0.25em] text-stone">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Where can Alex reach you?"
                    className={field}
                  />
                </div>
                <div>
                  <label htmlFor="pathway" className="text-xs uppercase tracking-[0.25em] text-stone">
                    I'm reaching out as
                  </label>
                  <select
                    id="pathway"
                    data-testid="contact-pathway-select"
                    value={form.pathway}
                    onChange={(e) => setForm({ ...form, pathway: e.target.value })}
                    className={`${field} appearance-none cursor-pointer`}
                  >
                    {pathways.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="text-xs uppercase tracking-[0.25em] text-stone">
                    Where are you right now?
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="As much or as little as you'd like to share. There's no wrong way to begin."
                    className={`${field} resize-none`}
                  />
                </div>
                {error && (
                  <p data-testid="contact-error" className="text-sm text-clay">{error}</p>
                )}
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="group inline-flex items-center gap-3 bg-ink text-paper text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-clay transition-colors duration-300 disabled:opacity-60"
                >
                  {status === "sending" ? "Sending…" : "Start the Conversation"}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </main>
  );
}
